import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Copy, Check, Download, Share2 } from "lucide-react";
import { Overline } from "../components/site/primitives";
import { generateSurvivalCard } from "../components/site/essenceCard";

const API = process.env.REACT_APP_BACKEND_URL;

const FIRST_QUESTION = {
  type: "question",
  section: "angry",
  intro:
    "Let's start with the stuff that pisses you off. Think about the recurring situations, people, frustrations, disappointments, or circumstances in your life that make you angry. What do you complain about? What feels unfair? What should other people be doing differently? Who keeps letting you down?",
  question: "What are you pissed off about?",
  helper:
    "Don't make yourself sound enlightened. For this exercise, blame people. Be dramatic. Be petty if necessary. What is wrong, whose fault is it, and why?",
};

const SECTION_META = {
  angry: { label: "The Angry List", progress: 0.12 },
  sad: { label: "The Sad List", progress: 0.28 },
  early: { label: "Early Pattern", progress: 0.44 },
  become: { label: "Who Do You Become?", progress: 0.6 },
  complaints: { label: "Recurring Complaints", progress: 0.74 },
  fear: { label: "The Core Fear", progress: 0.88 },
};

const THINKING = ["Listening...", "Following the thread...", "Going a layer deeper...", "Noticing a pattern..."];

const INTRO_VERBS = ["control", "perform", "disappear", "please", "prove", "avoid", "fix", "push", "rescue", "shut down"];

const MECH_FIELDS = [
  { key: "activates", label: "What flips her on" },
  { key: "does", label: "What she does" },
  { key: "protectsFrom", label: "What she's protecting you from" },
  { key: "contradiction", label: "The contradiction inside her" },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

const Thinking = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % THINKING.length), 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="sm-loading" className="flex min-h-[40vh] flex-col items-center justify-center text-center">
      <div className="h-px w-16 overflow-hidden bg-ink/10">
        <motion.div className="h-px w-16 bg-terracotta" animate={{ x: ["-100%", "100%"] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.6 }}
          className="mt-8 font-display text-2xl font-medium italic leading-snug text-ink md:text-3xl"
        >
          {THINKING[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
};

const questionToText = (q) => [q.intro, q.question, q.helper].filter(Boolean).join("\n");

const buildCopyText = (final, essenceWords) => {
  const parts = [
    "MY SURVIVAL MECHANISMS",
    final.mechanisms.map((m) => m.name.toUpperCase()).join(" · "),
    "",
    ...final.mechanisms.map(
      (m) =>
        `${m.name.toUpperCase()}\nWhat flips her on: ${m.activates}\nWhat she does: ${m.does}\nWhat she's protecting you from: ${m.protectsFrom}\nThe contradiction: ${m.contradiction}`
    ),
    "",
    `THE SURVIVAL SYSTEM\n${final.survivalSystem}`,
    "",
    `WHO YOU ARE VS. WHO TAKES OVER\nMy Essence: ${essenceWords.join(" · ")}\n${final.essenceVsSurvival}`,
    "",
    `WHEN SHE GETS NEAR YOUR MONEY\n${final.money}`,
    "",
    "Distinguished through Creating Wealth Through Community.",
  ];
  return parts.join("\n\n");
};

export default function SurvivalMechanism() {
  const [stage, setStage] = useState("intro"); // intro | essence | question | final
  const [essenceWords, setEssenceWords] = useState(["", "", "", "", ""]);
  const [step, setStep] = useState(FIRST_QUESTION);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [finalResult, setFinalResult] = useState(null);
  const [refineMode, setRefineMode] = useState(null); // null | "replace" | "rename"
  const [refineIndex, setRefineIndex] = useState(null);
  const [refineText, setRefineText] = useState("");
  const [cardUrl, setCardUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const transcript = useRef([]);

  useEffect(() => {
    if (finalResult) {
      setCardUrl(null);
      generateSurvivalCard(finalResult.mechanisms.map((m) => m.name))
        .then(setCardUrl)
        .catch(() => setCardUrl(null));
    }
  }, [finalResult]);

  const callStep = async (messages) => {
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/survival/step`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      if (data.type === "final") {
        try {
          localStorage.setItem("cwc_sm_names", JSON.stringify(data.mechanisms.map((m) => m.name)));
        } catch {}
        const base = messages.filter((m) => !m.content.startsWith("[CURRENT RESULT JSON]"));
        transcript.current = [...base, { role: "guide", content: `[CURRENT RESULT JSON]: ${JSON.stringify(data)}` }];
        setFinalResult(data);
        setRefineMode(null);
        setRefineIndex(null);
        setRefineText("");
        setStage("final");
      } else {
        transcript.current = [...messages, { role: "guide", content: questionToText(data) }];
        setStep(data);
        setAnswer("");
        setStage("question");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message || "We lost the thread for a moment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const essenceReady = essenceWords.every((w) => w.trim().length > 0);

  const begin = () => {
    transcript.current = [
      { role: "participant", content: `[PARTICIPANT'S FIVE ESSENCE WORDS]: ${essenceWords.map((w) => w.trim()).join(", ")}` },
      { role: "guide", content: questionToText(FIRST_QUESTION) },
    ];
    setStep(FIRST_QUESTION);
    setAnswer("");
    setStage("question");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitAnswer = () => {
    if (answer.trim().length < 3 || busy) return;
    callStep([...transcript.current, { role: "participant", content: answer.trim() }]);
  };

  const submitRefine = () => {
    if (refineIndex === null || refineText.trim().length < 3 || busy) return;
    const tag = refineMode === "rename" ? "RENAME MECHANISM" : "ONE DOESN'T FIT — MECHANISM";
    callStep([...transcript.current, { role: "participant", content: `[${tag} ${refineIndex + 1}]: ${refineText.trim()}` }]);
  };

  const restart = () => {
    transcript.current = [];
    setStage("intro");
    setEssenceWords(["", "", "", "", ""]);
    setStep(FIRST_QUESTION);
    setAnswer("");
    setFinalResult(null);
    setCardUrl(null);
    setRefineMode(null);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copyResults = async () => {
    const text = buildCopyText(finalResult, essenceWords.map((w) => w.trim()));
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadCard = () => {
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = "my-survival-mechanisms.png";
    a.click();
  };

  const shareCard = async () => {
    try {
      const blob = await (await fetch(cardUrl)).blob();
      const file = new File([blob], "my-survival-mechanisms.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Survival Mechanisms" });
        return;
      }
    } catch (e) {
      if (e.name === "AbortError") return;
    }
    downloadCard();
  };

  const progress = SECTION_META[step.section]?.progress || 0.1;

  return (
    <div data-testid="sm-page" className="relative min-h-screen overflow-x-clip bg-ivory">
      <div className="grain-overlay" />
      <main className="relative mx-auto max-w-[860px] px-6 py-20 md:px-10 md:py-28">
        <AnimatePresence mode="wait">
          {stage === "intro" && (
            <motion.div key="intro" {...fade} data-testid="sm-intro">
              <Overline>Become Unmistakable</Overline>
              <h1 className="mt-6 font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                Meet Your <span className="italic text-terracotta">Survival Mechanism</span>
              </h1>
              <p className="mt-8 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">
                There is a version of you that knows exactly how to get you back to familiar.
              </p>
              <p className="mt-6 max-w-md font-display text-xl italic leading-loose text-ink md:text-2xl">
                It may {INTRO_VERBS.map((v, i) => (
                  <span key={v}>
                    <span className={i % 2 === 0 ? "text-terracotta" : ""}>{v}</span>
                    {i < INTRO_VERBS.length - 1 ? ". " : "."}
                  </span>
                ))}
              </p>
              <div className="mt-6 max-w-xl space-y-4 font-sans text-lg font-light leading-relaxed text-stonegray">
                <p>It isn't bad. At some point, it probably helped.</p>
                <p>
                  But what once protected you can quietly become the thing limiting what you are willing to risk,
                  receive, create, ask for, or become.
                </p>
                <p className="text-ink">
                  We're going to find yours. And then we're going to give her a name.
                </p>
              </div>
              <button
                data-testid="sm-start-btn"
                onClick={() => {
                  setStage("essence");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="group mt-10 inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
              >
                <span>Meet My Survival Mechanism</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
              </button>
            </motion.div>
          )}

          {stage === "essence" && (
            <motion.div key="essence" {...fade} data-testid="sm-essence">
              <Overline>First, Your Essence</Overline>
              <h2 className="mt-6 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink sm:text-4xl md:text-5xl">
                Before we meet your <span className="italic text-terracotta">Survival Mechanisms...</span>
              </h2>
              <p className="mt-6 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">
                Your Essence is who you are underneath the strategies, defenses, performance and protection.
              </p>
              <p className="mt-4 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">
                Enter the five Essence words you discovered in the previous exercise.
              </p>
              <div className="mt-10 space-y-4">
                {essenceWords.map((w, i) => (
                  <div key={i} className="flex items-center gap-5">
                    <span className="w-24 shrink-0 font-sans text-[0.65rem] font-medium uppercase tracking-[0.24em] text-terracotta">
                      Essence {i + 1}
                    </span>
                    <input
                      data-testid={`sm-essence-input-${i + 1}`}
                      value={w}
                      onChange={(e) => {
                        const next = [...essenceWords];
                        next[i] = e.target.value;
                        setEssenceWords(next);
                      }}
                      placeholder="e.g. Heart"
                      className="w-full border-b border-ink/20 bg-transparent px-1 py-3 font-display text-2xl text-ink placeholder:text-stonegray/40 focus:border-terracotta focus:outline-none"
                    />
                  </div>
                ))}
              </div>
              <p className="mt-6 max-w-xl font-sans text-sm font-light leading-relaxed text-stonegray">
                Haven't done Discover Your Essence yet?{" "}
                <a href="/essence" className="text-terracotta underline underline-offset-4 hover:text-ink" data-testid="sm-essence-link">
                  Start there first.
                </a>
              </p>
              <button
                data-testid="sm-essence-continue-btn"
                onClick={begin}
                disabled={!essenceReady}
                className={`group mt-10 inline-flex items-center gap-3 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] transition-[transform,background-color] duration-300 ${
                  essenceReady
                    ? "bg-terracotta text-white hover:-translate-y-0.5 hover:bg-ink"
                    : "cursor-not-allowed bg-ink/10 text-ink/40"
                }`}
              >
                <span>Continue</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
              </button>
            </motion.div>
          )}

          {stage === "question" && !busy && (
            <motion.div key={`q-${transcript.current.length}`} {...fade} data-testid="sm-question">
              <div className="flex items-center justify-between">
                <Overline>{SECTION_META[step.section]?.label || "The Exercise"}</Overline>
              </div>
              <div className="mt-4 h-px w-full bg-ink/10">
                <motion.div
                  className="h-px bg-terracotta"
                  initial={false}
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              {step.intro && (
                <p className="mt-10 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">{step.intro}</p>
              )}
              <h2 className="mt-6 whitespace-pre-line font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink sm:text-4xl md:text-5xl">
                {step.question}
              </h2>
              {step.helper && (
                <p className="mt-5 max-w-xl border-l-2 border-terracotta/60 pl-4 font-sans text-sm font-light leading-relaxed text-stonegray">
                  {step.helper}
                </p>
              )}
              <textarea
                data-testid="sm-answer-input"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Say it how it actually feels..."
                className="mt-8 min-h-[180px] w-full resize-y border border-ink/15 bg-alabaster/60 p-6 font-sans text-base font-light leading-relaxed text-ink placeholder:text-stonegray/60 focus:border-terracotta focus:outline-none"
              />
              {error && (
                <p data-testid="sm-error" className="mt-4 border-l-2 border-terracotta pl-4 font-sans text-sm font-light text-ink">{error}</p>
              )}
              <button
                data-testid="sm-continue-btn"
                onClick={submitAnswer}
                disabled={answer.trim().length < 3}
                className={`group mt-6 inline-flex items-center gap-3 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] transition-[transform,background-color] duration-300 ${
                  answer.trim().length >= 3
                    ? "bg-terracotta text-white hover:-translate-y-0.5 hover:bg-ink"
                    : "cursor-not-allowed bg-ink/10 text-ink/40"
                }`}
              >
                <span>Continue</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
              </button>
            </motion.div>
          )}

          {busy && (
            <motion.div key="busy" {...fade}>
              <Thinking />
            </motion.div>
          )}

          {stage === "final" && finalResult && !busy && (
            <motion.div key="final" {...fade} data-testid="sm-final">
              <Overline>Your Survival Mechanisms</Overline>
              <h2 className="mt-6 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink sm:text-4xl md:text-5xl">
                I think I've met <span className="italic text-terracotta">all three of them.</span>
              </h2>

              <div className="mt-14 space-y-16">
                {finalResult.mechanisms.map((m, i) => (
                  <motion.div
                    key={m.name}
                    data-testid={`sm-mechanism-${i + 1}`}
                    initial={{ opacity: 0, y: 26 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 + i * 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="border-t border-ink/10 pt-10"
                  >
                    <span className="font-sans text-[0.65rem] font-medium uppercase tracking-[0.3em] text-terracotta">
                      Mechanism {["One", "Two", "Three"][i]}
                    </span>
                    <h3
                      data-testid={`sm-mechanism-name-${i + 1}`}
                      className="mt-4 font-display text-3xl font-medium uppercase leading-[1.05] tracking-tight text-ink sm:text-4xl md:text-5xl"
                    >
                      {m.name}
                    </h3>
                    <div className="mt-8 space-y-6">
                      {MECH_FIELDS.map((f) => (
                        <div key={f.key}>
                          <p className="font-sans text-xs font-medium uppercase tracking-[0.24em] text-stonegray">{f.label}</p>
                          <p className="mt-2 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">
                            <span className="sr-only">{f.label}: </span>
                            <span className="text-ink/90">{m[f.key]}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }}>
                <div className="mt-20">
                  <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">The Survival System</h3>
                  <p className="mt-3 font-sans text-xs font-medium uppercase tracking-[0.26em] text-terracotta">
                    Fear → Protection → Relief → Repeat
                  </p>
                  <p data-testid="sm-survival-system" className="mt-4 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">
                    {finalResult.survivalSystem}
                  </p>
                </div>

                <div className="mt-20 border-y border-ink/10 py-12">
                  <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">
                    Who You Are vs. <span className="italic text-terracotta">Who Takes Over</span>
                  </h3>
                  <div className="mt-8 grid gap-10 md:grid-cols-2">
                    <div>
                      <p className="font-sans text-xs font-medium uppercase tracking-[0.26em] text-terracotta">Your Essence</p>
                      <p data-testid="sm-essence-line" className="mt-3 font-display text-xl font-medium leading-relaxed text-ink md:text-2xl">
                        {essenceWords.map((w) => w.trim()).filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div>
                      <p className="font-sans text-xs font-medium uppercase tracking-[0.26em] text-stonegray">Your Survival Mechanisms</p>
                      <div className="mt-3 space-y-1">
                        {finalResult.mechanisms.map((m) => (
                          <p key={m.name} className="font-display text-xl font-medium leading-relaxed text-ink/70 md:text-2xl">{m.name}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p data-testid="sm-essence-vs" className="mt-8 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">
                    {finalResult.essenceVsSurvival}
                  </p>
                  <p className="mt-10 font-display text-3xl font-medium uppercase tracking-tight text-terracotta md:text-4xl">
                    Who's driving?
                  </p>
                </div>

                <div className="mt-16">
                  <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">
                    When She Gets Near <span className="italic text-terracotta">Your Money</span>
                  </h3>
                  <p data-testid="sm-money" className="mt-4 max-w-2xl font-sans text-lg font-light leading-relaxed text-stonegray">
                    {finalResult.money}
                  </p>
                </div>

                {/* Refinement */}
                <div className="mt-16 border border-ink/12 bg-alabaster/60 p-8 md:p-10" data-testid="sm-refine-section">
                  <p className="font-display text-xl font-medium text-ink md:text-2xl">Not quite right?</p>
                  <div className="mt-5 flex flex-wrap gap-4">
                    <button
                      data-testid="sm-refine-replace-btn"
                      onClick={() => {
                        setRefineMode(refineMode === "replace" ? null : "replace");
                        setRefineIndex(null);
                        setRefineText("");
                      }}
                      className={`border px-6 py-3 font-sans text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                        refineMode === "replace" ? "border-terracotta text-terracotta" : "border-ink/25 text-ink hover:border-terracotta hover:text-terracotta"
                      }`}
                    >
                      One of these doesn't fit
                    </button>
                    <button
                      data-testid="sm-refine-rename-btn"
                      onClick={() => {
                        setRefineMode(refineMode === "rename" ? null : "rename");
                        setRefineIndex(null);
                        setRefineText("");
                      }}
                      className={`border px-6 py-3 font-sans text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                        refineMode === "rename" ? "border-terracotta text-terracotta" : "border-ink/25 text-ink hover:border-terracotta hover:text-terracotta"
                      }`}
                    >
                      Help me rename one
                    </button>
                  </div>
                  {refineMode && (
                    <div className="mt-6">
                      <p className="font-sans text-sm font-light text-stonegray">
                        {refineMode === "replace" ? "Which one doesn't fit?" : "Which one needs a better name?"}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {finalResult.mechanisms.map((m, i) => (
                          <button
                            key={m.name}
                            data-testid={`sm-refine-pick-${i + 1}`}
                            onClick={() => setRefineIndex(i)}
                            className={`border px-5 py-2.5 font-display text-base transition-colors duration-300 ${
                              refineIndex === i ? "border-terracotta bg-terracotta/10 text-ink" : "border-ink/20 text-ink/70 hover:border-ink/50"
                            }`}
                          >
                            {m.name}
                          </button>
                        ))}
                      </div>
                      <textarea
                        data-testid="sm-refine-input"
                        value={refineText}
                        onChange={(e) => setRefineText(e.target.value)}
                        placeholder={refineMode === "replace" ? "What feels wrong or missing about it?" : "What do you love about the pattern — and what's off about the name?"}
                        className="mt-4 min-h-[100px] w-full resize-y border border-ink/15 bg-ivory p-5 font-sans text-base font-light leading-relaxed text-ink placeholder:text-stonegray/60 focus:border-terracotta focus:outline-none"
                      />
                      {error && (
                        <p data-testid="sm-error" className="mt-3 border-l-2 border-terracotta pl-4 font-sans text-sm font-light text-ink">{error}</p>
                      )}
                      <button
                        data-testid="sm-refine-submit-btn"
                        onClick={submitRefine}
                        disabled={refineIndex === null || refineText.trim().length < 3}
                        className={`group mt-4 inline-flex items-center gap-3 px-7 py-3.5 font-sans text-xs font-medium uppercase tracking-[0.2em] transition-[transform,background-color] duration-300 ${
                          refineIndex !== null && refineText.trim().length >= 3
                            ? "bg-terracotta text-white hover:-translate-y-0.5 hover:bg-ink"
                            : "cursor-not-allowed bg-ink/10 text-ink/40"
                        }`}
                      >
                        <span>{refineMode === "replace" ? "Find a Truer One" : "Find a Better Name"}</span>
                        <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Card */}
                {cardUrl && (
                  <div data-testid="sm-card-section" className="mt-20">
                    <h3 className="font-display text-2xl font-medium leading-snug text-ink md:text-3xl">Your Survival Mechanism card</h3>
                    <p className="mt-3 max-w-xl font-sans text-base font-light leading-relaxed text-stonegray">
                      A keepsake of all three names — so the next time one of them grabs the wheel, you can call her by name.
                    </p>
                    <div className="mt-8 grid items-start gap-10 md:grid-cols-2">
                      <img
                        src={cardUrl}
                        alt="Your Survival Mechanism card"
                        data-testid="sm-card-preview"
                        className="w-full max-w-sm shadow-[0_30px_70px_-30px_rgba(26,26,24,0.5)]"
                      />
                      <div className="flex flex-col items-start gap-4 md:pt-4">
                        <button
                          data-testid="sm-card-download-btn"
                          onClick={downloadCard}
                          className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
                        >
                          <Download className="h-4 w-4" strokeWidth={1.6} />
                          <span>Download My Card</span>
                        </button>
                        <button
                          data-testid="sm-card-share-btn"
                          onClick={shareCard}
                          className="group inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-ink transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                        >
                          <Share2 className="h-4 w-4" strokeWidth={1.6} />
                          <span>Share My Card</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-20 border-y border-ink/10 py-12 text-center">
                  <p className="mx-auto max-w-xl whitespace-pre-line font-display text-xl italic leading-relaxed text-ink md:text-2xl">
                    {"Your Survival Mechanisms may still show up.\nProbably loudly."}
                  </p>
                  <p className="mx-auto mt-6 max-w-xl font-sans text-base font-light leading-relaxed text-stonegray">
                    The goal isn't to kill them. It's to recognize them. Name them. Laugh when appropriate. Thank them
                    for trying to protect you. And then decide whether they get the wheel.
                  </p>
                </div>

                <div className="mt-12 flex flex-wrap items-center gap-4">
                  <button
                    data-testid="sm-restart-btn"
                    onClick={restart}
                    className="group inline-flex items-center gap-3 border border-ink/25 px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-ink transition-[transform,border-color,color] duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
                  >
                    <RotateCcw className="h-4 w-4" strokeWidth={1.6} />
                    <span>Start Again</span>
                  </button>
                  <button
                    data-testid="sm-copy-btn"
                    onClick={copyResults}
                    className="group inline-flex items-center gap-3 bg-terracotta px-8 py-4 font-sans text-sm font-medium uppercase tracking-[0.14em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink"
                  >
                    {copied ? <Check className="h-4 w-4" strokeWidth={1.8} /> : <Copy className="h-4 w-4" strokeWidth={1.6} />}
                    <span>{copied ? "Copied" : "Copy My Survival Mechanism Results"}</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
